import client from 'client'
import type {
  UpdateManualIssueIssueIssueIdPutMutationRequest,
  UpdateManualIssueIssueIssueIdPutMutationResponse,
  UpdateManualIssueIssueIssueIdPutPathParams,
  UpdateManualIssueIssueIssueIdPutQueryParams,
  UpdateManualIssueIssueIssueIdPut422,
} from '../../types/UpdateManualIssueIssueIssueIdPut.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const updateManualIssueIssueIssueIdPutMutationKey = () => [{ url: '/issue/{issue_id}' }] as const

export type UpdateManualIssueIssueIssueIdPutMutationKey = ReturnType<typeof updateManualIssueIssueIssueIdPutMutationKey>

/**
 * @summary Update Manual Issue
 * {@link /issue/:issue_id}
 */
export async function updateManualIssueIssueIssueIdPut(
  issue_id: UpdateManualIssueIssueIssueIdPutPathParams['issue_id'],
  data: UpdateManualIssueIssueIssueIdPutMutationRequest,
  params?: UpdateManualIssueIssueIssueIdPutQueryParams,
  config: Partial<RequestConfig<UpdateManualIssueIssueIssueIdPutMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    UpdateManualIssueIssueIssueIdPutMutationResponse,
    ResponseErrorConfig<UpdateManualIssueIssueIssueIdPut422>,
    UpdateManualIssueIssueIssueIdPutMutationRequest
  >({ method: 'PUT', url: `/issue/${issue_id}`, params, data, ...requestConfig })
  return res
}

/**
 * @summary Update Manual Issue
 * {@link /issue/:issue_id}
 */
export function useUpdateManualIssueIssueIssueIdPut(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<UpdateManualIssueIssueIssueIdPutMutationResponse>,
      ResponseErrorConfig<UpdateManualIssueIssueIssueIdPut422>,
      {
        issue_id: UpdateManualIssueIssueIssueIdPutPathParams['issue_id']
        data: UpdateManualIssueIssueIssueIdPutMutationRequest
        params?: UpdateManualIssueIssueIssueIdPutQueryParams
      }
    >
    client?: Partial<RequestConfig<UpdateManualIssueIssueIssueIdPutMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? updateManualIssueIssueIssueIdPutMutationKey()

  return useMutation<
    ResponseConfig<UpdateManualIssueIssueIssueIdPutMutationResponse>,
    ResponseErrorConfig<UpdateManualIssueIssueIssueIdPut422>,
    {
      issue_id: UpdateManualIssueIssueIssueIdPutPathParams['issue_id']
      data: UpdateManualIssueIssueIssueIdPutMutationRequest
      params?: UpdateManualIssueIssueIssueIdPutQueryParams
    }
  >({
    mutationFn: async ({ issue_id, data, params }) => {
      return updateManualIssueIssueIssueIdPut(issue_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}